import axios from "axios";
import {
  clearSessionFromInterceptor,
  getStoredUserToken,
  isUnauthorizedStatus,
} from "@/lib/sessionAuth";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function forceLogoutOn401(error) {
  if (isUnauthorizedStatus(error?.response?.status)) {
    clearSessionFromInterceptor();
  }
  return Promise.reject(error);
}

export const api = axios.create({ baseURL: BASE });
api.interceptors.request.use((cfg) => {
  const token = getStoredUserToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});
api.interceptors.response.use((r) => r, forceLogoutOn401);

export const adminApi = axios.create({ baseURL: BASE });
adminApi.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("rl_admin_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});
adminApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (isUnauthorizedStatus(error?.response?.status)) {
      try {
        localStorage.removeItem("rl_admin_token");
      } catch {
        /* ignore */
      }
    }
    return Promise.reject(error);
  }
);

export const money = (cents) => {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
};

export const imgSrc = (u) => {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

export const uploadFile = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

// Admin-scoped upload — uses the admin token so super_admin can attach images
// while editing a business they don't own (hits /admin/uploads).
export const uploadFileAdmin = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await adminApi.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

export const SMS_FAIL_USER_MSG =
  "Couldn't send verification SMS — check your number";

export const apiErr = (e) => {
  const status = e?.response?.status;
  const headers = e?.response?.headers || {};
  const orig = headers["x-do-orig-status"] || headers["X-Do-Orig-Status"];
  const data = e?.response?.data;
  const d = data?.detail;

  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");

  // DigitalOcean's edge rewrites our JSON 503 into an HTML 504/503 page, so
  // axios never sees FastAPI's detail. Map that back to the SMS failure copy.
  const html = typeof data === "string" ? data : "";
  const url = `${e?.config?.baseURL || ""}${e?.config?.url || ""}`;
  const smsRelated = /otp\/request|\/branches\/[^/]+\/claim/.test(url);
  const doRewrote503 =
    String(orig) === "503" ||
    /via_upstream|App Platform failed/i.test(html) ||
    ((status === 504 || status === 503) && /text\/html/i.test(headers["content-type"] || ""));

  if (smsRelated && (doRewrote503 || status === 503 || status === 504)) {
    return SMS_FAIL_USER_MSG;
  }
  if (doRewrote503 && (status === 504 || status === 503)) {
    return SMS_FAIL_USER_MSG;
  }

  return e?.message || "Something went wrong";
};